import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, MessageSquare, FileText, Briefcase, Users, RefreshCw, Trash2, ChevronDown, ChevronUp, Search, ShieldCheck, ExternalLink } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { adminLogin, adminMe, adminStats, adminList, adminUpdateStatus, adminDelete, getToken, setToken, clearToken } from "../lib/adminApi";
import { getErrorMessage } from "../lib/api";
import { cn } from "../lib/utils";

const STATUSES = {
  contacts: ["new", "in_progress", "replied", "closed"],
  quotes: ["new", "in_progress", "proposal_sent", "won", "lost"],
  applications: ["received", "screening", "interview", "offered", "rejected"],
};
const STATUS_COLOR = { new: "bg-brand-orange/15 text-orange-700", received: "bg-brand-orange/15 text-orange-700", in_progress: "bg-sky-100 text-sky-700", screening: "bg-sky-100 text-sky-700", replied: "bg-emerald-100 text-emerald-700", proposal_sent: "bg-violet-100 text-violet-700", interview: "bg-violet-100 text-violet-700", won: "bg-emerald-100 text-emerald-700", offered: "bg-emerald-100 text-emerald-700", closed: "bg-slate-100 text-slate-600", lost: "bg-rose-100 text-rose-700", rejected: "bg-rose-100 text-rose-700" };
const TABS = [
  { key: "contacts", label: "Contacts", icon: MessageSquare },
  { key: "quotes", label: "Quotes", icon: FileText },
  { key: "applications", label: "Applications", icon: Briefcase },
  { key: "subscribers", label: "Subscribers", icon: Users },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
const label = (s) => (s || "").replace(/_/g, " ");

const LoginCard = ({ onSuccess }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(u, p);
      setToken(res.token);
      toast.success("Welcome back");
      onSuccess();
    } catch (err) {
      toast.error(getErrorMessage(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="admin-scope flex min-h-screen items-center justify-center bg-slate-50 px-4" dir="ltr">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl shadow-brand-navy/5 ring-1 ring-slate-100 sm:p-10" data-testid="admin-login-card">
        <img src="/logo.png" alt="Jonaren" className="h-12 w-auto" />
        <div className="mt-6 flex items-center gap-2 text-brand-teal"><ShieldCheck className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-[0.2em]">Admin Portal</span></div>
        <h1 className="mt-2 font-heading text-2xl font-bold text-brand-navy">Sign in to dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">Manage enquiries, quotes and applications.</p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div className="space-y-1.5"><Label htmlFor="ad-user">Username</Label><Input id="ad-user" required value={u} onChange={(e) => setU(e.target.value)} autoComplete="username" className="h-12" data-testid="admin-username-input" /></div>
          <div className="space-y-1.5"><Label htmlFor="ad-pass">Password</Label><Input id="ad-pass" type="password" required value={p} onChange={(e) => setP(e.target.value)} autoComplete="current-password" className="h-12" data-testid="admin-password-input" /></div>
          <button type="submit" disabled={loading} className="btn-primary w-full bg-brand-navy text-white hover:bg-slate-800 disabled:opacity-60" data-testid="admin-login-button">{loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</> : "Sign In"}</button>
        </form>
        <a href="/" className="mt-6 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-teal"><ExternalLink className="h-3.5 w-3.5" /> Back to website</a>
      </div>
    </div>
  );
};

const Row = ({ item, collection, onStatus, onDelete }) => {
  const [open, setOpen] = useState(false);
  const statuses = STATUSES[collection];
  const primary = collection === "applications" ? item.position : collection === "quotes" ? item.service : item.subject || "(no subject)";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md" data-testid={`admin-row-${collection}`}>
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex flex-1 items-start gap-3 text-left">
          <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-teal/10 text-sm font-bold text-brand-teal">{(item.name || item.email || "?").charAt(0).toUpperCase()}</span>
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-brand-navy">{item.name || item.email}</span>
              {item.status && <Badge className={cn("rounded-full border-0 capitalize", STATUS_COLOR[item.status] || "bg-slate-100 text-slate-600")}>{label(item.status)}</Badge>}
            </span>
            {collection !== "subscribers" && <span className="mt-0.5 block truncate text-sm text-slate-600">{primary}</span>}
            <span className="mt-0.5 block text-xs text-slate-400">{item.email} &middot; {fmt(item.created_at)}</span>
          </span>
        </button>
        <div className="flex items-center gap-2 sm:shrink-0">
          {statuses && (
            <Select value={item.status} onValueChange={(v) => onStatus(item.id, v)}>
              <SelectTrigger className="h-9 w-[150px] capitalize" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{label(s)}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <button type="button" onClick={() => onDelete(item)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600" aria-label="Delete" data-testid="admin-delete-button"><Trash2 className="h-4 w-4" /></button>
          {collection !== "subscribers" && <button type="button" onClick={() => setOpen((o) => !o)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500" aria-label="Toggle details">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>}
        </div>
      </div>
      {open && collection !== "subscribers" && (
        <div className="grid gap-3 border-t border-slate-100 bg-slate-50/60 p-4 text-sm sm:grid-cols-2" data-testid="admin-row-details">
          {[["Email", item.email], ["Phone", item.phone], ["Company", item.company], ["Budget", item.budget], ["Subject", item.subject], ["Position", item.position], ["LinkedIn", item.linkedin], ["Reference", item.id]].filter(([, v]) => v).map(([k, v]) => (
            <div key={k}><p className="text-xs font-bold uppercase tracking-wider text-slate-400">{k}</p><p className="mt-0.5 break-all text-slate-700">{v}</p></div>
          ))}
          {(item.message || item.cover_letter) && <div className="sm:col-span-2"><p className="text-xs font-bold uppercase tracking-wider text-slate-400">{item.cover_letter ? "Cover letter" : "Message"}</p><p className="mt-1 whitespace-pre-wrap leading-relaxed text-slate-700">{item.message || item.cover_letter}</p></div>}
          <div className="sm:col-span-2"><a href={`mailto:${item.email}?subject=Re: ${encodeURIComponent(primary || "Your enquiry")}`} className="inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-teal-dark"><Mail className="h-4 w-4" /> Reply by email</a></div>
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("contacts");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(null);

  const load = async (which = tab) => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([adminStats(), adminList(which)]);
      setStats(s);
      setItems(list);
    } catch (err) {
      if (err?.response?.status === 401) { toast.error("Session expired. Please sign in again."); onLogout(); }
      else toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => items.filter((i) => JSON.stringify(i).toLowerCase().includes(q.toLowerCase())), [items, q]);

  const onStatus = async (id, status) => {
    try {
      const updated = await adminUpdateStatus(tab, id, status);
      setItems((list) => list.map((i) => (i.id === id ? updated : i)));
      toast.success(`Status updated to ${label(status)}`);
      adminStats().then(setStats).catch(() => {});
    } catch (err) { toast.error(getErrorMessage(err)); }
  };
  const confirmDelete = async () => {
    if (!pending) return;
    try {
      await adminDelete(tab, pending.id);
      setItems((list) => list.filter((i) => i.id !== pending.id));
      toast.success("Record deleted");
      adminStats().then(setStats).catch(() => {});
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setPending(null); }
  };

  return (
    <div className="admin-scope min-h-screen bg-slate-50" dir="ltr" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-4"><img src="/logo.png" alt="Jonaren" className="h-9 w-auto" /><span className="hidden text-xs font-bold uppercase tracking-[0.2em] text-brand-teal sm:block">Admin Dashboard</span></div>
          <div className="flex items-center gap-2">
            <a href="/" className="hidden items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-teal sm:inline-flex"><ExternalLink className="h-4 w-4" /> View site</a>
            <button type="button" onClick={() => load()} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:text-brand-teal" aria-label="Refresh" data-testid="admin-refresh-button"><RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /></button>
            <button type="button" onClick={onLogout} className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-navy px-3 text-sm font-semibold text-white hover:bg-slate-800" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
          {TABS.map((tb) => {
            const s = stats?.[tb.key];
            return (
              <button type="button" key={tb.key} onClick={() => setTab(tb.key)} className={cn("rounded-2xl border bg-white p-5 text-left transition-shadow hover:shadow-md", tab === tb.key ? "border-brand-teal ring-2 ring-brand-teal/20" : "border-slate-200")} data-testid={`admin-stat-${tb.key}`}>
                <div className="flex items-center justify-between"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal"><tb.icon className="h-5 w-5" /></span>{s?.new > 0 && <span className="rounded-full bg-brand-orange px-2 py-0.5 text-xs font-bold text-white">{s.new} new</span>}</div>
                <p className="mt-4 font-heading text-3xl font-bold text-brand-navy">{s ? s.total : "-"}</p>
                <p className="text-sm text-slate-500">{tb.label}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-auto flex-wrap bg-white p-1 ring-1 ring-slate-200">
              {TABS.map((tb) => <TabsTrigger key={tb.key} value={tb.key} className="data-[state=active]:bg-brand-navy data-[state=active]:text-white" data-testid={`admin-tab-${tb.key}`}>{tb.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <label className="relative block w-full md:max-w-xs"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search records..." className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20" data-testid="admin-search-input" /></label>
        </div>

        <div className="mt-5 space-y-3" data-testid="admin-list">
          {loading ? <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            : filtered.length === 0 ? <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-500" data-testid="admin-empty">No records found.</div>
            : filtered.map((item) => <Row key={item.id} item={item} collection={tab} onStatus={onStatus} onDelete={setPending} />)}
        </div>
      </main>

      <AlertDialog open={!!pending} onOpenChange={(v) => !v && setPending(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this record?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the record for {pending?.name || pending?.email}. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="admin-confirm-delete">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const Admin = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    document.title = "Admin | Jonaren Technologies";
    if (!getToken()) { setAuthed(false); return; }
    adminMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);
  const logout = () => { clearToken(); setAuthed(false); navigate("/admin"); };
  if (authed === null) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-brand-teal" /></div>;
  return authed ? <Dashboard onLogout={logout} /> : <LoginCard onSuccess={() => setAuthed(true)} />;
};
